<template>
  <div class="content mb-6 mt-8">
    <div class="password-tip">
      <p><i>*</i>请输入 6 位数字授权密码：</p>
      <div class="eye-icon" @click="togglePassword">
        <EyeOutlined v-if="showPassword" />
        <EyeInvisibleOutlined v-else />
      </div>
    </div>
    <input type="password" style="display: none" autocomplete="new-password" />
    <div class="otp-container">
      <input
        v-for="(_, index) in 6"
        :key="index"
        :ref="(el) => (inputRefs[index] = el ? el as HTMLInputElement : null)"
        v-model="codeArray[index]"
        :type="showPassword ? 'tel' : 'tel'"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="1"
        class="otp-input"
        :class="{ 'password-hidden': !showPassword }"
        :style="getFocusStyle()"
        @input="handleInput(index)"
        @keydown="handleKeydown($event, index)"
        @paste="handlePaste"
        autocomplete="one-time-code"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";

const props = defineProps({
  color: {
    type: String,
    default: "#4749ad", // 默认主题色
  },
});

const emit = defineEmits(["update:code"]);

const showPassword = ref(false);
const inputRefs = ref<(HTMLInputElement | null)[]>([]);
const codeArray = ref<string[]>(Array(6).fill(""));

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

// 关键修改3：暴露主动聚焦方法，供弹窗打开后调用
const focusFirstInput = () => {
  nextTick(() => {
    // 加少量延时兼容部分移动端机型
    setTimeout(() => {
      const firstInput = inputRefs.value[0];
      if (firstInput) {
        firstInput.focus();
        firstInput.click();
      }
    }, 100);
  });
};

// 组件挂载时的聚焦（非弹窗场景）
onMounted(() => {
  focusFirstInput();
});

const getFocusStyle = () => {
  return {
    "--focus-border-color": props.color,
  };
};

const handleInput = (index: number) => {
  // 确保输入的是数字
  codeArray.value[index] = codeArray.value[index].replace(/[^0-9]/g, "");

  // 自动跳到下一个输入框
  if (codeArray.value[index] && index < 5) {
    inputRefs.value[index + 1]?.focus();
  }

  emit("update:code", codeArray.value);
};

const handleKeydown = (event: KeyboardEvent, index: number) => {
  if (event.key === "Backspace" && !codeArray.value[index] && index > 0) {
    inputRefs.value[index - 1]?.focus();
  }
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedText = event.clipboardData?.getData("text") || "";
  const numbers = pastedText
    .replace(/[^0-9]/g, "")
    .split("")
    .slice(0, 6);

  numbers.forEach((num, index) => {
    if (index < 6) {
      codeArray.value[index] = num;
    }
  });

  // 聚焦到最后一个填充的输入框的下一个，或第一个空输入框
  const lastFilledIndex = codeArray.value.findLastIndex((val) => val !== "");
  const focusIndex = Math.min(lastFilledIndex + 1, 5);
  inputRefs.value[focusIndex]?.focus();

  emit("update:code", codeArray.value);
};
</script>

<style scoped>
.content {
  margin-top: 20px;
}

.otp-container {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 6px;
  /* 移动端gap也做自适应 */
  gap: clamp(8px, 3vw, 12px);
}

.otp-input {
  /* 基础样式：使用vw做自适应，同时限制最小/最大值 */
  width: clamp(20px, 6vw, 40px);
  height: clamp(20px, 6vw, 40px);
  padding: 8px;
  /* 字体大小也自适应 */
  font-size: clamp(18px, 5vw, 30px);
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  text-align: center;
  outline: none;
  transition: all 0.3s;
  border-radius: 4px;
  -webkit-appearance: none;
  appearance: none;
}

/* 模拟password的隐藏效果 */
.password-hidden {
  -webkit-text-security: disc;
  text-security: disc;
}

/* 移动端适配：统一用相对单位做自适应，不再写固定px */
@media (max-width: 768px) {
  .otp-input {
    /* 移动端基于视口宽度自适应，clamp(最小值, 自适应值, 最大值) */
    width: clamp(18px, 6vw, 30px);
    height: clamp(18px, 6vw, 30px);
    font-size: clamp(16px, 4vw, 24px);
  }

  /* 第三个输入框的间距也自适应 */
  .otp-input:nth-of-type(3) {
    margin-right: clamp(16px, 8vw, 32px);
  }
}

/* 针对小屏手机（如320px）的精细化适配（可选） */
@media (max-width: 375px) {
  .otp-input {
    width: clamp(16px, 4.5vw, 25px);
    height: clamp(16px, 4.5vw, 25px);
    font-size: clamp(14px, 3.5vw, 20px);
  }
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 80px white inset !important;
  -webkit-text-fill-color: #333 !important;
}

.otp-input:focus {
  border-color: var(--focus-border-color, #4749ad);
}

/* 非移动端保留原间距，移动端已在@media里覆盖 */
.otp-input:nth-of-type(3) {
  margin-right: 32px;
}

.password-tip {
  display: flex;
  font-size: clamp(12px, 3vw, 14px);
  justify-content: space-between;
  color: rgba(0, 0, 0, 0.55);
}

.password-tip i {
  color: red;
  font-style: normal;
}

.password-tip .eye-icon {
  color: #c0c2c9;
  cursor: pointer;
}
</style>
